import { MedplumClient } from '@medplum/core';
import { DocumentReference } from '@medplum/fhirtypes';
import { ReportTemplate, TiptapJSON } from '../types/template.types';
import { ARKON, TIPTAP_CONTENT_EXTENSION_URL } from '@/consts/systems.consts';

/**
 * Service for managing report templates and saved report content in Medplum
 */
export class TemplateService {
  constructor(private medplum: MedplumClient) {}

  /**
   * Save a report template to Medplum
   * @param template - Report template to save
   * @returns Created or updated DocumentReference
   */
  async saveTemplate(template: ReportTemplate): Promise<DocumentReference> {
    const jsonString = JSON.stringify(template);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const file = new File([blob], `template-${template.id}.json`, {
      type: 'application/json',
    });

    // Create binary resource with the template JSON
    const binary = await this.medplum.createBinary({
      data: file,
      filename: `template-${template.id}.json`,
      contentType: 'application/json',
    });

    // Check if template already exists
    const existing = await this.findTemplateDocRef(template.id);

    if (existing) {
      // Update existing DocumentReference
      return this.medplum.updateResource({
        ...existing,
        content: [
          {
            attachment: {
              url: `Binary/${binary.id}`,
              contentType: 'application/json',
              title: template.name,
            },
          },
        ],
        description: template.description || template.name,
      });
    }

    // Create new DocumentReference
    return this.medplum.createResource({
      resourceType: 'DocumentReference',
      status: 'current',
      type: {
        coding: [
          {
            system: ARKON.CS.DOCUMENT.TYPE.BASE,
            code: ARKON.CS.DOCUMENT.TYPE.CODES.REPORT_TEMPLATE,
            display: 'Report Template',
          },
        ],
      },
      identifier: [
        {
          system: `${ARKON.TEMPLATE.ID}`,
          value: template.id,
        },
      ],
      description: template.description || template.name,
      content: [
        {
          attachment: {
            url: `Binary/${binary.id}`,
            contentType: 'application/json',
            title: template.name,
          },
        },
      ],
    });
  }

  /**
   * Load a template by its ID
   * @param templateId - Unique template identifier
   * @returns Loaded template or null if not found
   */
  async loadTemplate(templateId: string): Promise<ReportTemplate | null> {
    const docRef = await this.findTemplateDocRef(templateId);
    if (!docRef) {
      return null;
    }

    return this.fetchTemplateContent(docRef);
  }

  /**
   * Load a template by DocumentReference ID
   * @param docRefId - DocumentReference resource ID
   * @returns Loaded template or null if not found
   */
  async loadTemplateByDocRefId(
    docRefId: string
  ): Promise<ReportTemplate | null> {
    try {
      const docRef = await this.medplum.readResource(
        'DocumentReference',
        docRefId
      );
      return await this.fetchTemplateContent(docRef);
    } catch {
      return null;
    }
  }

  /**
   * List all available templates
   * @returns Array of template metadata
   */
  async listTemplates(): Promise<
    { id: string; name: string; docRefId: string }[]
  > {
    const bundle = await this.medplum.search('DocumentReference', {
      type: `${ARKON.CS.DOCUMENT.TYPE.BASE}|${ARKON.CS.DOCUMENT.TYPE.CODES.REPORT_TEMPLATE}`,
      status: 'current',
    });

    const templates: { id: string; name: string; docRefId: string }[] = [];

    for (const entry of bundle.entry || []) {
      const docRef = entry.resource as DocumentReference;
      const templateId = docRef.identifier?.find(
        (id) => id.system === `${ARKON.TEMPLATE.ID}`
      )?.value;

      if (templateId && docRef.id) {
        templates.push({
          id: templateId,
          name: docRef.description || templateId,
          docRefId: docRef.id,
        });
      }
    }

    return templates;
  }

  /**
   * Load saved report content for a DiagnosticReport
   * @param reportId - DiagnosticReport resource ID
   * @returns Saved content or null if not found
   */
  async loadReportContent(reportId: string): Promise<TiptapJSON | undefined> {
    const diagnosticReport = await this.medplum.readResource(
      'DiagnosticReport',
      reportId
    );

    const content = diagnosticReport.extension?.find(
      (ext) => ext.url === TIPTAP_CONTENT_EXTENSION_URL
    );
    if (!content?.valueString) {
      return undefined;
    }

    return JSON.parse(content.valueString);
  }

  /**
   * Find template DocumentReference by template ID
   * @param templateId - Unique template identifier
   * @returns DocumentReference or null if not found
   */
  private async findTemplateDocRef(
    templateId: string
  ): Promise<DocumentReference | null> {
    const bundle = await this.medplum.search('DocumentReference', {
      identifier: `${ARKON.TEMPLATE.ID}|${templateId}`,
      status: 'current',
    });

    return (bundle.entry?.[0]?.resource as DocumentReference) || null;
  }

  /**
   * Find report content DocumentReference by report ID
   * @param reportId - DiagnosticReport resource ID
   * @returns DocumentReference or null if not found
   */
  private async findReportContentDocRef(
    reportId: string
  ): Promise<DocumentReference | null> {
    const bundle = await this.medplum.search('DocumentReference', {
      identifier: `${ARKON.REPORT_CONTENT.ID}|${reportId}`,
      status: 'current',
    });

    return (bundle.entry?.[0]?.resource as DocumentReference) || null;
  }

  /**
   * Fetch and parse template content from DocumentReference
   * @param docRef - DocumentReference containing template data
   * @returns Parsed template or null on error
   */
  private async fetchTemplateContent(
    docRef: DocumentReference
  ): Promise<ReportTemplate | null> {
    return this.fetchJsonContent<ReportTemplate>(docRef);
  }

  /**
   * Generic JSON content fetcher
   * @param docRef - DocumentReference with JSON content
   * @returns Parsed JSON content or null on error
   */
  private async fetchJsonContent<T>(
    docRef: DocumentReference
  ): Promise<T | null> {
    const binaryUrl = docRef.content?.[0]?.attachment?.url;
    if (!binaryUrl) {
      return null;
    }

    try {
      // Handle both relative and absolute URLs
      let fetchUrl = binaryUrl;
      if (binaryUrl.startsWith('Binary/')) {
        const binaryId = binaryUrl.replace('Binary/', '');
        const binary = await this.medplum.readResource('Binary', binaryId);
        fetchUrl = binary.url || '';
      }

      if (!fetchUrl) {
        return null;
      }

      const response = await fetch(fetchUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch content: ${response.statusText}`);
      }

      const jsonText = await response.text();
      return JSON.parse(jsonText) as T;
    } catch (error) {
      console.error('Error fetching JSON content:', error);
      return null;
    }
  }
}

/**
 * Create a template service instance
 * @param medplum - Medplum client instance
 * @returns New TemplateService instance
 */
export function createTemplateService(medplum: MedplumClient): TemplateService {
  return new TemplateService(medplum);
}
